/**
 * CL-PORTAL-FILES-001 — Canonical project file domain + audience (pure).
 *
 * Domain = where the file appears in the portal.
 * Audience = who among eligible module users may see it.
 * One primary portal domain per file (no dual render unless intentionally designed).
 */

import { isControlledProjectDocument } from "./controlledProjectDocuments";
import { isEngineeringSupportingDocument } from "./engineeringDrawingReviewModel";
import { isPlanningProjectPhotoFile } from "./planningProjectPhotosModel";
import { isDrawingReviewDomainFile } from "./clientDrawingReviewGroups";

/** Local helpers — avoid circular import with clientProjectDocuments. */
function isClientDocumentLibrary(file) {
  return safeStr(file?.libraryType).toLowerCase() === "client_document";
}

function isActiveClientDocumentLibrary(file) {
  if (!isClientDocumentLibrary(file)) return false;
  if (safeStr(file?.status).toLowerCase() === "deleted") return false;
  return safeStr(file?.documentStatus).toLowerCase() !== "archived";
}

function isClientReferencePhotoRow(file) {
  if (!file || safeStr(file?.status).toLowerCase() === "deleted") return false;
  if (safeStr(file?.libraryType).toLowerCase() !== "photo") return false;
  if (safeStr(file?.subcategory).toLowerCase() === "reference") return true;
  if (safeStr(file?.clientUploadSource).toLowerCase() === "client_portal") return true;
  return false;
}

export const PROJECT_FILE_DOMAINS = Object.freeze({
  DRAWING_REVIEW: "drawing_review",
  PROJECT_PHOTO: "project_photo",
  REQUESTED_DOCUMENT: "requested_document",
  CLIENT_SUPPORTING_DOCUMENT: "client_supporting_document",
  ENGINEERING_SUPPORTING_DOCUMENT: "engineering_supporting_document",
  CONTROLLED_DOCUMENT: "controlled_document",
  GENERAL_PROJECT_FILE: "general_project_file",
  ADMIN_ONLY: "admin_only",
});

export const PROJECT_FILE_AUDIENCE = Object.freeze({
  PROJECT_MEMBERS: "project_members",
  SELECTED_COLLABORATORS: "selected_collaborators",
  CLIENT_ONLY: "client_only",
  ADMIN_ONLY: "admin_only",
});

export const PROJECT_FILE_AUDIENCE_LABELS = Object.freeze({
  [PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS]: "Available to project members",
  [PROJECT_FILE_AUDIENCE.SELECTED_COLLABORATORS]: "Shared with selected people",
  [PROJECT_FILE_AUDIENCE.CLIENT_ONLY]: "Client only",
  [PROJECT_FILE_AUDIENCE.ADMIN_ONLY]: "Admin only",
});

export const PROJECT_FILE_AUDIENCE_EDITOR_OPTIONS = Object.freeze({
  project_photo: [
    { value: PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS, label: "Project members with Photos access" },
    { value: PROJECT_FILE_AUDIENCE.SELECTED_COLLABORATORS, label: "Selected people only" },
    { value: PROJECT_FILE_AUDIENCE.CLIENT_ONLY, label: "Client only" },
    { value: PROJECT_FILE_AUDIENCE.ADMIN_ONLY, label: "Admin only" },
  ],
  documents: [
    { value: PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS, label: "Project members with Documents access" },
    { value: PROJECT_FILE_AUDIENCE.SELECTED_COLLABORATORS, label: "Selected people only" },
    { value: PROJECT_FILE_AUDIENCE.CLIENT_ONLY, label: "Client only" },
    { value: PROJECT_FILE_AUDIENCE.ADMIN_ONLY, label: "Admin only" },
  ],
  drawing_review: [
    { value: PROJECT_FILE_AUDIENCE.SELECTED_COLLABORATORS, label: "Selected people only (review package)" },
    { value: PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS, label: "Project members with Drawings access" },
    { value: PROJECT_FILE_AUDIENCE.CLIENT_ONLY, label: "Client only" },
    { value: PROJECT_FILE_AUDIENCE.ADMIN_ONLY, label: "Admin only" },
  ],
});

function safeStr(value) {
  return String(value ?? "").trim();
}

function normEmail(value) {
  return safeStr(value).toLowerCase();
}

function isLikelyImage(file = {}) {
  const t = safeStr(file?.contentType || file?.type || file?.mimeType).toLowerCase();
  if (t.startsWith("image/")) return true;
  const name = safeStr(file?.name).toLowerCase();
  return /\.(png|jpg|jpeg|webp|gif|heic)$/i.test(name);
}

/**
 * Canonical primary portal domain for a pmProjectFiles row.
 * Explicit metadata wins; legacy inference is deterministic.
 */
export function resolveProjectFileDomain(file = {}) {
  if (!file || typeof file !== "object") return PROJECT_FILE_DOMAINS.GENERAL_PROJECT_FILE;
  if (safeStr(file?.status).toLowerCase() === "deleted") return PROJECT_FILE_DOMAINS.ADMIN_ONLY;

  const explicit = safeStr(file?.projectFileDomain || file?.fileDomain).toLowerCase();
  if (Object.values(PROJECT_FILE_DOMAINS).includes(explicit)) return explicit;

  const visibility = safeStr(file?.visibility).toLowerCase();
  if (visibility === "admin" || visibility === "internal") return PROJECT_FILE_DOMAINS.ADMIN_ONLY;
  if (file?.clientVisible === false && !isClientDocumentLibrary(file)) {
    // Hidden from clients; may still be collaborator-visible — keep domain by type below.
  }

  if (isControlledProjectDocument(file)) return PROJECT_FILE_DOMAINS.CONTROLLED_DOCUMENT;

  // Photos win over Documents even when also tagged as client reference uploads.
  if (isPlanningProjectPhotoFile(file) || isClientReferencePhotoRow(file)) {
    return PROJECT_FILE_DOMAINS.PROJECT_PHOTO;
  }
  if (isLikelyImage(file) && safeStr(file?.libraryType).toLowerCase() === "photo") {
    return PROJECT_FILE_DOMAINS.PROJECT_PHOTO;
  }

  if (isEngineeringSupportingDocument(file)) {
    return PROJECT_FILE_DOMAINS.ENGINEERING_SUPPORTING_DOCUMENT;
  }

  if (isClientDocumentLibrary(file) || isActiveClientDocumentLibrary(file)) {
    if (safeStr(file?.clientDocumentRequestId)) {
      return PROJECT_FILE_DOMAINS.REQUESTED_DOCUMENT;
    }
    return PROJECT_FILE_DOMAINS.CLIENT_SUPPORTING_DOCUMENT;
  }

  if (isDrawingReviewDomainFile(file)) return PROJECT_FILE_DOMAINS.DRAWING_REVIEW;

  const lt = safeStr(file?.libraryType).toLowerCase();
  if (lt === "drawing" || lt === "plan" || lt === "sheet") {
    return PROJECT_FILE_DOMAINS.DRAWING_REVIEW;
  }

  return PROJECT_FILE_DOMAINS.GENERAL_PROJECT_FILE;
}

export function normalizeProjectFileAudience(raw) {
  const key = safeStr(raw).toLowerCase();
  if (Object.values(PROJECT_FILE_AUDIENCE).includes(key)) return key;
  return "";
}

/**
 * Resolve audience. Does NOT infer project_members from accessScope/full_project.
 * Legacy fallback preserves existing product behavior and marks diagnostics.
 */
export function resolveProjectFileAudience(file = {}, { domain = "" } = {}) {
  const explicit = normalizeProjectFileAudience(file?.projectFileAudience || file?.fileAudience);
  if (explicit) {
    return { audience: explicit, legacyFallback: false, source: "projectFileAudience" };
  }

  const visibility = safeStr(file?.visibility).toLowerCase();
  if (visibility === "admin" || visibility === "internal") {
    return { audience: PROJECT_FILE_AUDIENCE.ADMIN_ONLY, legacyFallback: true, source: "visibility" };
  }

  const resolvedDomain = domain || resolveProjectFileDomain(file);

  if (file?.clientVisible === false) {
    return { audience: PROJECT_FILE_AUDIENCE.ADMIN_ONLY, legacyFallback: true, source: "clientVisible_false" };
  }

  // Engineer package / shares remain selected_collaborators for drawings by default.
  if (resolvedDomain === PROJECT_FILE_DOMAINS.DRAWING_REVIEW) {
    return {
      audience: PROJECT_FILE_AUDIENCE.SELECTED_COLLABORATORS,
      legacyFallback: true,
      source: "drawing_review_default",
    };
  }

  // Project photos historically project-visible when clientVisible !== false.
  if (resolvedDomain === PROJECT_FILE_DOMAINS.PROJECT_PHOTO) {
    if (file?.clientVisible === true || !("clientVisible" in (file || {}))) {
      return {
        audience: PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS,
        legacyFallback: true,
        source: "photo_legacy_project_members",
      };
    }
  }

  // Client documents: do NOT silently become project-wide for collaborators.
  if (
    resolvedDomain === PROJECT_FILE_DOMAINS.CLIENT_SUPPORTING_DOCUMENT ||
    resolvedDomain === PROJECT_FILE_DOMAINS.REQUESTED_DOCUMENT
  ) {
    return {
      audience: PROJECT_FILE_AUDIENCE.CLIENT_ONLY,
      legacyFallback: true,
      source: "client_document_default",
    };
  }

  if (resolvedDomain === PROJECT_FILE_DOMAINS.ENGINEERING_SUPPORTING_DOCUMENT) {
    return {
      audience: PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS,
      legacyFallback: true,
      source: "engineering_supporting_default",
    };
  }

  if (resolvedDomain === PROJECT_FILE_DOMAINS.CONTROLLED_DOCUMENT) {
    return {
      audience: PROJECT_FILE_AUDIENCE.CLIENT_ONLY,
      legacyFallback: true,
      source: "controlled_document_default",
    };
  }

  return {
    audience: PROJECT_FILE_AUDIENCE.SELECTED_COLLABORATORS,
    legacyFallback: true,
    source: "safe_default_selected",
  };
}

export function labelProjectFileAudienceDiagnostic(file = {}, context = {}) {
  const domain = resolveProjectFileDomain(file);
  const { audience } = resolveProjectFileAudience(file, { domain });
  if (context?.uploadedByViewer) return "Uploaded by you";
  if (context?.hasDirectShare) return "Shared with you";
  return PROJECT_FILE_AUDIENCE_LABELS[audience] || "Legacy visibility";
}

function shareMatchesViewer(share, { viewerUid = "", viewerEmail = "" } = {}) {
  const uid = safeStr(viewerUid);
  const email = normEmail(viewerEmail);
  if (!share) return false;
  if (uid && safeStr(share.sharedWithUid) === uid) return true;
  if (email && normEmail(share.sharedWithEmail || share.sharedWithEmailLower) === email) return true;
  return false;
}

/**
 * Whether a viewer may see a file in a portal module (domain already correct).
 * Module permission must be checked by the caller for project_members.
 */
export function viewerCanSeeProjectFile({
  file = null,
  viewerUid = "",
  viewerEmail = "",
  isAdmin = false,
  isClientPersona = false,
  isProjectMember = false,
  hasModulePermission = false,
  shares = [],
  shareByFileId = null,
} = {}) {
  if (!file || safeStr(file?.status).toLowerCase() === "deleted") return false;
  if (isAdmin) return true;

  const domain = resolveProjectFileDomain(file);
  const { audience } = resolveProjectFileAudience(file, { domain });
  const fid = safeStr(file?.id);
  const own =
    (safeStr(viewerUid) && safeStr(file?.uploadedByUid) === safeStr(viewerUid)) ||
    (normEmail(viewerEmail) && normEmail(file?.uploadedByEmail) === normEmail(viewerEmail));

  if (own) return true;

  if (audience === PROJECT_FILE_AUDIENCE.ADMIN_ONLY) return false;

  if (audience === PROJECT_FILE_AUDIENCE.CLIENT_ONLY) {
    return Boolean(isClientPersona);
  }

  if (audience === PROJECT_FILE_AUDIENCE.PROJECT_MEMBERS) {
    return Boolean(isProjectMember) && Boolean(hasModulePermission);
  }

  // selected_collaborators
  let share = null;
  if (shareByFileId && fid) share = shareByFileId[fid] || null;
  if (!share && Array.isArray(shares)) {
    share =
      shares.find((s) => safeStr(s?.fileId) === fid && shareMatchesViewer(s, { viewerUid, viewerEmail })) ||
      null;
  }
  return Boolean(share);
}

/**
 * Documents & requests list for a portal viewer — never includes project photos or drawings.
 */
export function filterDocumentsRequestsForViewer({
  files = [],
  viewerUid = "",
  viewerEmail = "",
  isAdmin = false,
  isClientPersona = false,
  isProjectMember = true,
  canUploadFiles = false,
  canViewDocuments = true,
  shares = [],
  shareByFileId = null,
} = {}) {
  const moduleOk = Boolean(canUploadFiles || canViewDocuments || isClientPersona || isAdmin);
  return (Array.isArray(files) ? files : []).filter((file) => {
    const domain = resolveProjectFileDomain(file);
    if (domain === PROJECT_FILE_DOMAINS.PROJECT_PHOTO) return false;
    if (domain === PROJECT_FILE_DOMAINS.DRAWING_REVIEW) return false;
    if (domain === PROJECT_FILE_DOMAINS.ADMIN_ONLY) return false;
    if (
      domain !== PROJECT_FILE_DOMAINS.REQUESTED_DOCUMENT &&
      domain !== PROJECT_FILE_DOMAINS.CLIENT_SUPPORTING_DOCUMENT &&
      domain !== PROJECT_FILE_DOMAINS.ENGINEERING_SUPPORTING_DOCUMENT &&
      domain !== PROJECT_FILE_DOMAINS.CONTROLLED_DOCUMENT &&
      domain !== PROJECT_FILE_DOMAINS.GENERAL_PROJECT_FILE
    ) {
      return false;
    }

    // Controlled docs have their own surface; keep only when already client-visible pipeline uses them.
    if (domain === PROJECT_FILE_DOMAINS.CONTROLLED_DOCUMENT) return false;

    return viewerCanSeeProjectFile({
      file,
      viewerUid,
      viewerEmail,
      isAdmin,
      isClientPersona,
      isProjectMember,
      hasModulePermission: moduleOk,
      shares,
      shareByFileId,
    });
  });
}

/**
 * Photos gallery domain filter — project_photo only.
 */
export function filterProjectPhotosForViewer(files = []) {
  return (Array.isArray(files) ? files : []).filter(
    (file) => resolveProjectFileDomain(file) === PROJECT_FILE_DOMAINS.PROJECT_PHOTO
  );
}

/**
 * When merging share-scoped portal loads, which client_document rows to include for a collaborator.
 * Engineers must not receive unrelated client uploads merely because rules allow member reads.
 */
export function shouldMergeClientDocumentIntoCollaboratorLibrary(file = {}, { viewerUid = "", viewerEmail = "" } = {}) {
  const domain = resolveProjectFileDomain(file);
  if (domain === PROJECT_FILE_DOMAINS.PROJECT_PHOTO) return false;
  if (domain === PROJECT_FILE_DOMAINS.ENGINEERING_SUPPORTING_DOCUMENT) {
    const own =
      (safeStr(viewerUid) && safeStr(file?.uploadedByUid) === safeStr(viewerUid)) ||
      (normEmail(viewerEmail) && normEmail(file?.uploadedByEmail) === normEmail(viewerEmail));
    const src = safeStr(file?.clientUploadSource).toLowerCase();
    return own || src === "engineer_portal";
  }
  if (
    domain === PROJECT_FILE_DOMAINS.CLIENT_SUPPORTING_DOCUMENT ||
    domain === PROJECT_FILE_DOMAINS.REQUESTED_DOCUMENT
  ) {
    const own =
      (safeStr(viewerUid) && safeStr(file?.uploadedByUid) === safeStr(viewerUid)) ||
      (normEmail(viewerEmail) && normEmail(file?.uploadedByEmail) === normEmail(viewerEmail));
    return own;
  }
  return false;
}

export function portalModuleForDomain(domain) {
  const d = safeStr(domain);
  if (d === PROJECT_FILE_DOMAINS.DRAWING_REVIEW) return "drawings_review";
  if (d === PROJECT_FILE_DOMAINS.PROJECT_PHOTO) return "photos";
  if (
    d === PROJECT_FILE_DOMAINS.REQUESTED_DOCUMENT ||
    d === PROJECT_FILE_DOMAINS.CLIENT_SUPPORTING_DOCUMENT ||
    d === PROJECT_FILE_DOMAINS.ENGINEERING_SUPPORTING_DOCUMENT
  ) {
    return "documents_requests";
  }
  if (d === PROJECT_FILE_DOMAINS.CONTROLLED_DOCUMENT) return "controlled_documents";
  return "none";
}
